import { handleResponse } from "@/services/axios";
import ReferralService from "@/services/api/referral";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  LeadItem,
  ReferralBusinessResponse,
  ReferralPayload,
  ReferralUserItem,
} from "@/types/ReferralProgramtype";

interface LoadingState {
  referral: boolean;
  export : boolean;
  saveBusiness: boolean;
}

export interface ReferralUserCountByStatus {
  pending: number;
  approved: number;
  rejected: number;
  activeUsers: number;
  inActiveUsers: number;
  applied: number;
  accepted: number;
}

interface MyReferralState {
  myReferrals: any[];
  referralUserCount : ReferralUserCountByStatus | null;
  referralBusinessResponse: ReferralBusinessResponse;
  myReferral: any;
  totalCount: number;
  loading: LoadingState;
  error: string | null;
  referralApproval: null;
  leads: LeadItem[];
  referralUsers: ReferralUserItem[];
}

const initialState: MyReferralState = {
  myReferrals: [],
  referralUserCount: null,
  referralBusinessResponse: {} as ReferralBusinessResponse,
  myReferral: null,
  totalCount: 0,
  error: null,
  loading: {
    referral: false,
    export : false,
    saveBusiness: false
  },
  referralApproval: null,
  leads: [],
  referralUsers: [],
};

export const getMyReferrals = createAsyncThunk(
  "referrals/myReferrals",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await ReferralService.Referrals(params);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch my referrals");
    }
  },
);

export const getMyReferralById = createAsyncThunk(
  "referrals/referralDetails",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await ReferralService.getReferralDetailById(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch my referral detail",
      );
    }
  },
);
export const deleteReferralById = createAsyncThunk(
  "referrals/deleteReferral",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await ReferralService.deleteReferralById(id);
      handleResponse(response, rejectWithValue);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to delete my referral detail",
      );
    }
  },
);

export const updateReferral = createAsyncThunk(
  "referrals/updateReferral",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await ReferralService.updateReferral(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update referral");
    }
  },
);
export const addReferral = createAsyncThunk(
  "referrals/addReferral",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await ReferralService.addReferral(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add referral");
    }
  },
);

export const approvalReferral = createAsyncThunk(
  "referrals/approveReferral",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await ReferralService.approvalReferral(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to approve approve",
      );
    }
  },
);
//Referral Business

export const getReferralBusiness = createAsyncThunk(
  "referrals/referralBusiness",
  async (payload: ReferralPayload, { rejectWithValue }) => {
    try {
      const res = await ReferralService.referralBusiness(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to fetch referral business",
      );
    }
  },
);

export const getLeadList = createAsyncThunk(
  "referrals/leadList",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await ReferralService.leadList(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to fetch lead list",
      );
    }
  },
);

export const getReferralUsers = createAsyncThunk(
  "referrals/referralUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await ReferralService.referralUsers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      rejectWithValue(
        err?.response?.data?.message || "Failed to fetch referral users",
      );
    }
  },
);

export const addReferralBusiness = createAsyncThunk(
  "referrals/addReferralBusiness",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await ReferralService.addReferralBuisness(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add referral");
    }
  },
);

export const updateReferralBusiness = createAsyncThunk(
  "referrals/updateReferralBusiness",
  async (data : any , { rejectWithValue }) => {
    try {
      const response = await ReferralService.updateReferralBusiness(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update referral");
    }
  },
);

export const exportReferralBusiness = createAsyncThunk(
  "referrals/exportReferralBusiness",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await ReferralService.exportReferralBusinessInExcel(payload);
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to export referral business");
    }
  },
);

const ReferralProgramSlice = createSlice({
  name: "referrals",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMyReferrals.pending, (state) => {
        state.loading.referral = true;
        state.error = null;
        state.myReferrals = [];
      })
      .addCase(getMyReferrals.fulfilled, (state, action) => {
        state.loading.referral = false;
        state.myReferrals = action.payload || [];
      })
      .addCase(getMyReferrals.rejected, (state, action) => {
        state.loading.referral = false;
        state.myReferrals = [];
        state.error = action.payload as string;
      })
      .addCase(getMyReferralById.fulfilled, (state, action) => {
        state.myReferral = action.payload || null;
      })
      .addCase(approvalReferral.fulfilled, (state, action) => {
        state.referralApproval = action.payload || null;
        state.loading.referral = false;
      })
      .addCase(approvalReferral.pending, (state, action) => {
        state.loading.referral = true;
      })
      .addCase(approvalReferral.rejected, (state, action) => {
        state.loading.referral = false;
      })
      .addCase(getReferralBusiness.pending, (state) => {
        state.loading.referral = true;
        state.error = null;
      })
      .addCase(getReferralBusiness.fulfilled, (state, action) => {
        state.loading.referral = false;
        state.referralBusinessResponse = action.payload || null;
      })
      .addCase(getReferralBusiness.rejected, (state, action) => {
        state.loading.referral = false;
        state.error = action.payload as string;
      })
      .addCase(getLeadList.fulfilled, (state, action) => {
        state.loading.referral = false;
        state.leads = action.payload.Leads || [];
      })
      .addCase(getReferralUsers.fulfilled, (state, action) => {
        state.loading.referral = false;
        state.referralUsers = action.payload.results || [];
      })
      .addCase(exportReferralBusiness.pending, (state) => {
        state.loading.export = true;
      })
      .addCase(exportReferralBusiness.fulfilled, (state) => {
        state.loading.export = false;
      })
      .addCase(exportReferralBusiness.rejected, (state) => {
        state.loading.export = false;
      })
      .addCase(addReferralBusiness.pending, (state) => {
        state.loading.saveBusiness = true;
      })
      .addCase(addReferralBusiness.fulfilled, (state) => {
        state.loading.saveBusiness = false;
      })
      .addCase(addReferralBusiness.rejected, (state) => {
        state.loading.saveBusiness = false;
      })
      .addCase(updateReferralBusiness.pending, (state) => {
        state.loading.saveBusiness = true;
      })
      .addCase(updateReferralBusiness.fulfilled, (state) => {
        state.loading.saveBusiness = false;
      })
      .addCase(updateReferralBusiness.rejected, (state) => {
        state.loading.saveBusiness = false;
      })
      
  },
});

// export const { clearReferrals } = ReferralProgramSlice.actions;
export default ReferralProgramSlice.reducer;
